import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  MapPin,
  Send,
  Loader2,
  CheckCircle,
  MessageSquare,
  Clock,
  ArrowRight,
  HelpCircle
} from 'lucide-react';

const faqs = [
  { q: "Quel est le délai de réponse ?", a: "Nous répondons généralement sous 24h en jours ouvrés." },
  { q: "Comment exercer mes droits RGPD ?", a: "Envoyez un email à bonjour@10millionsinvestor.com avec votre demande. Nous traitons chaque requête sous 30 jours." },
  { q: "Quelle adresse utiliser pour la correspondance ?", a: "Pour toute correspondance officielle, merci d'utiliser exclusivement l'adresse bonjour@10millionsinvestor.com. C'est notre unique point de contact email." },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message envoyé !');
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez ou écrivez-nous directement à bonjour@10millionsinvestor.com.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4" data-testid="contact-success">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#00B050]/10 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-[#00B050]" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-3">Message envoyé</h2>
          <p className="text-slate-500 mb-8 leading-relaxed">
            Merci pour votre message. Nous vous répondrons dans les meilleurs délais, généralement sous 24h.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#0D47A1] font-semibold hover:underline"
            data-testid="contact-success-home-link"
          >
            Retour à l'accueil
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="contact-page">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_4ed8f34b-1bc9-42f5-8577-4843189c4948/artifacts/jd7g0w5b_LOGO%2010MILLIONSINVESTOR%20blanc.png"
              alt="10M Investor" className="h-7"
            />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors" data-testid="contact-back-link">
            <ChevronLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(13,71,161,0.3)_0%,_transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(0,176,80,0.15)_0%,_transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-slate-500 text-sm hover:text-slate-300 transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-slate-600" />
            <span className="text-[#00B050] text-sm font-medium">Contact</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">
            Parlons de<br />
            <span className="bg-gradient-to-r from-[#0D47A1] to-[#00B050] bg-clip-text text-transparent">votre projet.</span>
          </h1>
          <p className="text-slate-400 max-w-xl">
            Une question sur la plateforme, un partenariat ou simplement envie d'échanger ? Notre équipe est à votre écoute.
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left: Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact cards */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start gap-4 hover:shadow-lg hover:shadow-slate-100/50 transition-shadow duration-300" data-testid="contact-info-email">
              <div className="w-10 h-10 rounded-xl bg-[#0D47A1]/5 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#0D47A1]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm mb-1">Email</h3>
                <a href="mailto:bonjour@10millionsinvestor.com" className="text-[#0D47A1] text-sm hover:underline font-medium">
                  bonjour@10millionsinvestor.com
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start gap-4 hover:shadow-lg hover:shadow-slate-100/50 transition-shadow duration-300" data-testid="contact-info-location">
              <div className="w-10 h-10 rounded-xl bg-[#00B050]/5 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#00B050]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm mb-1">Localisation</h3>
                <p className="text-slate-500 text-sm">Lyon, France</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start gap-4 hover:shadow-lg hover:shadow-slate-100/50 transition-shadow duration-300" data-testid="contact-info-response">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm mb-1">Temps de réponse</h3>
                <p className="text-slate-500 text-sm">Sous 24h en jours ouvrés</p>
              </div>
            </div>

            {/* Notre équipe */}
            <div className="mt-2">
              <p className="text-sm font-bold text-slate-700 mb-3">Notre équipe</p>
              <div className="space-y-2.5">
                {[
                  { name: 'Arthur', role: 'Responsable partenariats', email: 'partenariat@10millionsinvestor.com', color: 'bg-[#0D47A1]/5 text-[#0D47A1]' },
                  { name: 'Alice', role: 'Responsable relation membres', email: 'membres@10millionsinvestor.com', color: 'bg-[#00B050]/5 text-[#00B050]' },
                  { name: 'Lucie', role: 'Support client & plateforme', email: 'support@10millionsinvestor.com', color: 'bg-violet-50 text-violet-600' },
                  { name: 'Marc', role: 'Chargé de relation client', email: 'bonjour@10millionsinvestor.com', color: 'bg-amber-50 text-amber-600' },
                ].map((member) => (
                  <div key={member.email} className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-md transition-shadow duration-300" data-testid={`contact-team-${member.name.toLowerCase()}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full ${member.color} flex items-center justify-center flex-shrink-0 font-bold text-sm`}>
                        {member.name[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{member.name} <span className="font-normal text-slate-400">— {member.role}</span></p>
                        <a href={`mailto:${member.email}`} className="text-xs text-[#0D47A1] hover:underline font-medium truncate block">{member.email}</a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="w-4 h-4 text-slate-400" />
                <p className="text-sm font-bold text-slate-700">Questions fréquentes</p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`contact-faq-${i}`}>
                    <p className="text-sm font-semibold text-slate-800 mb-1">{faq.q}</p>
                    <p className="text-sm text-slate-500">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 shadow-sm" data-testid="contact-form">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">Envoyez-nous un message</h2>
                  <p className="text-xs text-slate-500">Tous les champs marqués * sont obligatoires</p>
                </div>
              </div>

              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Nom complet *</label>
                    <Input
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                    <Input
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-slate-50 border-slate-200 focus:bg-white"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Sujet</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D47A1]/20"
                    data-testid="contact-subject"
                  >
                    <option value="">Choisir un sujet...</option>
                    <option value="general">Question générale</option>
                    <option value="partenariat">Partenariat</option>
                    <option value="bug">Signaler un problème</option>
                    <option value="suggestion">Suggestion d'amélioration</option>
                    <option value="presse">Presse & Médias</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label>
                  <textarea
                    placeholder="Décrivez votre demande en détail..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D47A1]/20 resize-none"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 text-sm font-semibold rounded-xl"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer le message
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2">
          <span>10 Millions Investor &copy; 2026</span>
          <div className="flex items-center gap-4">
            <Link to="/cgv" className="hover:text-slate-600 transition-colors">CGV</Link>
            <Link to="/confidentialite" className="hover:text-slate-600 transition-colors">Confidentialité</Link>
            <Link to="/charte" className="hover:text-slate-600 transition-colors">Charte</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
